/**
 * A PAUSED SOURCE, AS EVERY SOURCES SCREEN SHOWS IT — Library LB6b-T2,
 * acceptance L6b-08 (decision 33; founder decision OQ-L6b-1; ruling OQ-L6b-2).
 *
 * Three states a person must be able to tell apart, on the list card and on
 * the detail page alike (one component, so the two never word them
 * differently):
 *   - "Sign-in failed — reconnect": the source's sign-in failed
 *     (`pausedReason: 'credentials'`). Nothing runs it — not the schedule, not
 *     Sync now (409) — until it is reconnected; reconnecting clears it.
 *   - "Paused after failed syncs": failed runs in a row
 *     (`pausedReason: 'failures'`). The schedule skips it; Sync now is the
 *     retry, and a sync that succeeds resumes the schedule.
 *   - "Sync paused": a PERSON turned scheduled syncs off (`syncEnabled: false`).
 *     That is the person's switch, never the system's pause.
 */

import { AlertCircle, KeyRound } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { useStartOAuth, type ConnectorSummary } from '@/hooks/useConnectors';
import { LIBRARY_CONNECTOR_FAILURES_TO_PAUSE } from '@argusai/shared/schemas/library-connector.schema';

type PauseFields = Pick<ConnectorSummary, 'pausedReason' | 'pausedAt' | 'syncEnabled' | 'lastSyncError'>;

/** The badge beside a source's name: the system's pause, else the person's "Sync paused", else nothing. */
export function SourcePauseBadge({ connector }: { connector: PauseFields }) {
  if (connector.pausedReason === 'credentials') {
    return (
      <Badge variant="outline" className="text-red-600 border-red-300" title="Its sign-in failed. Reconnect the source to resume syncing.">
        Sign-in failed
      </Badge>
    );
  }
  if (connector.pausedReason === 'failures') {
    return (
      <Badge variant="outline" className="text-amber-600 border-amber-300" title="Scheduled syncs stopped after repeated failures.">
        Paused after failures
      </Badge>
    );
  }
  if (!connector.syncEnabled) {
    return <Badge variant="outline" title="Scheduled syncs were turned off by a person.">Sync paused</Badge>;
  }
  return null;
}

/** The reason, in words, with what to do about it. Nothing when the source is not paused by the system. */
export function SourcePauseNotice({ connector }: { connector: PauseFields }) {
  if (!connector.pausedReason) return null;
  const since = connector.pausedAt ? new Date(connector.pausedAt).toLocaleString() : null;
  if (connector.pausedReason === 'credentials') {
    return (
      <div
        role="status"
        data-testid="source-paused-credentials"
        className="text-xs text-red-600 bg-red-500/5 border border-red-500/20 rounded px-2 py-1.5 flex items-start gap-1.5"
      >
        <KeyRound className="h-3.5 w-3.5 shrink-0 mt-0.5" />
        <span>
          Sign-in failed — reconnect. This source is paused{since ? ` since ${since}` : ''}: its sign-in was refused, so
          nothing syncs it until it is reconnected.
        </span>
      </div>
    );
  }
  return (
    <div
      role="status"
      data-testid="source-paused-failures"
      className="text-xs text-amber-700 bg-amber-500/5 border border-amber-500/20 rounded px-2 py-1.5 flex items-start gap-1.5"
    >
      <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
      <span>
        Paused after {LIBRARY_CONNECTOR_FAILURES_TO_PAUSE} failed syncs in a row{since ? ` (since ${since})` : ''}. Scheduled
        syncs are stopped; Sync now retries, and a sync that succeeds resumes the schedule.
        {connector.lastSyncError ? ` Last error: ${connector.lastSyncError}` : ''}
      </span>
    </div>
  );
}

/**
 * Start the provider's sign-in for a source (POST …/oauth-start, write:library)
 * and send the browser to it. The one Connect / Reconnect action of the Sources
 * screens. LIB-04 — a deployment without provider OAuth credentials returns an
 * authUrl with a `_PLACEHOLDER` client_id (or none): say so instead of landing
 * the person on the provider's error page.
 */
export function useConnectSource(kindLabel: string) {
  const startOAuth = useStartOAuth();
  const connect = async (connectorId: string) => {
    try {
      const { authUrl } = await startOAuth.mutateAsync(connectorId);
      if (!authUrl || /_PLACEHOLDER|client_id=$|client_id=&/i.test(authUrl)) {
        toast.error(
          `${kindLabel} OAuth is not configured on this deployment. Ask your sysadmin to set the provider client_id and redirect_uri before connecting.`,
        );
        return;
      }
      // The provider's consent screen; its callback stores the credential
      // (encrypted, in the source's row) and clears a sign-in pause.
      window.location.href = authUrl;
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not start authentication');
    }
  };
  return { connect, isPending: startOAuth.isPending };
}
